import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const pageTitles = {
  '/dashboard': 'Dashboard',
  '/dashboard/website': 'Edit Website',
  '/dashboard/website/blogs': 'Blog',
  '/dashboard/website/social-links': 'Social & Chess Links',
  '/dashboard/website/contact': 'Contact Form',
  '/dashboard/website/donations': 'Donations',
  '/dashboard/website/shop': 'Shop',
  '/dashboard/website/premium': 'Premium / Plan',
  '/dashboard/website/referrals': 'Referrals',
  '/dashboard/website/theme': 'Theme & Design',
  '/dashboard/website/coach': 'Coaching',
  '/dashboard/coach': 'Coaching Admin',
  '/dashboard/analytics': 'Analytics',
  '/dashboard/tournaments': 'Tournaments',
  '/dashboard/settings': 'Account Settings',
  '/dashboard/user-guide': 'User Guide',
};

const DashboardTopBar = ({ onMenuToggle, userProfile, playerProfiles, selectedProfile, onProfileChange, onLogout, isAdmin, saveStatus }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);



  // Determine page title from route
  const getPageTitle = () => {
    // Check for coaching setup page
    if (location.pathname.startsWith('/dashboard/coach/')) {
      return 'Coaching Setup';
    }
    return pageTitles[location.pathname] || 'Dashboard';
  };

  return (
    <header className="h-14 border-b border-gray-800 bg-[#141414] flex items-center px-4 gap-3 flex-shrink-0">
      {/* Hamburger - mobile only */}
      <button
        onClick={onMenuToggle}
        className="lg:hidden p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
      >
        <i className="fa-solid fa-bars text-lg"></i>
      </button>

      {/* Page title */}
      <h1 className="text-white font-semibold text-lg truncate">
        {getPageTitle()}
      </h1>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Profile Selector (if multiple profiles) */}
      {playerProfiles?.length > 1 && (() => {
        const pinnedIds = userProfile?.pinnedProfiles || [];
        const pinned = pinnedIds.map(id => playerProfiles.find(p => p.id === id)).filter(Boolean);
        const unpinnedOwner = playerProfiles.filter(p => p.ownership === 'owner' && !pinnedIds.includes(p.id));
        const unpinnedShared = playerProfiles.filter(p => p.ownership === 'shared' && !pinnedIds.includes(p.id));
        const unpinnedAdmin = playerProfiles.filter(p => p.ownership === 'admin' && !pinnedIds.includes(p.id));

        return (
          <select
            value={selectedProfile?.id || ''}
            onChange={(e) => onProfileChange(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-1.5 text-sm"
          >
            {isAdmin ? (
              <>
                {pinned.length > 0 && (
                  <optgroup label="⭐ Pinned">
                    {pinned.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.displayName || profile.username}
                      </option>
                    ))}
                  </optgroup>
                )}
                {unpinnedOwner.length > 0 && (
                  <optgroup label="Your Sites">
                    {unpinnedOwner.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.displayName || profile.username}
                      </option>
                    ))}
                  </optgroup>
                )}
                {unpinnedShared.length > 0 && (
                  <optgroup label="Shared">
                    {unpinnedShared.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.displayName || profile.username}
                      </option>
                    ))}
                  </optgroup>
                )}
                {unpinnedAdmin.length > 0 && (
                  <optgroup label="Client Sites">
                    {unpinnedAdmin.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.displayName || profile.username}
                      </option>
                    ))}
                  </optgroup>
                )}
              </>
            ) : (
              playerProfiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.displayName || profile.username}
                </option>
              ))
            )}
          </select>
        );
      })()}

      {/* Save status indicator */}
      {saveStatus === 'dirty' && (
        <span className="flex items-center gap-1.5 text-xs text-amber-400">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
          Unsaved changes
        </span>
      )}
      {saveStatus === 'saving' && (
        <span className="flex items-center gap-1.5 text-xs text-gray-400">
          <i className="fa-solid fa-circle-notch fa-spin text-[10px]"></i>
          Saving...
        </span>
      )}
      {saveStatus === 'saved' && (
        <span className="flex items-center gap-1.5 text-xs text-green-400">
          <i className="fa-solid fa-check text-[10px]"></i>
          Autosaved
        </span>
      )}

      {/* User Guide */}
      <button
        onClick={() => navigate('/dashboard/user-guide')}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
      >
        <i className="fa-solid fa-book text-gray-500"></i>
        <span>User Guide</span>
      </button>

      {/* Account Dropdown */}
      <div className="relative">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-800 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
            {userProfile?.avatar ? (
              <img src={userProfile.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <i className="fas fa-user text-gray-400 text-xs"></i>
            )}
          </div>
          <svg
            className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-200 hidden sm:block ${isMenuOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isMenuOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsMenuOpen(false)}
            />
            <div className="absolute top-full right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg z-50 overflow-hidden border border-gray-700">
              <button
                onClick={() => {
                  navigate('/dashboard/settings');
                  setIsMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 font-medium transition-colors hover:bg-gray-700 text-gray-200"
              >
                <i className="fas fa-gear mr-2 text-gray-500"></i>
                Settings
              </button>
              <button
                onClick={() => {
                  onLogout();
                  setIsMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 text-red-400 font-medium transition-colors hover:bg-gray-700 border-t border-gray-700"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default DashboardTopBar;
